import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  isLoggedIn, clearToken, fetchCareers,
  createCareer, updateCareer, deleteCareer,
} from '../../api/client';
import AdminNav from './AdminNav';
import './Admin.css';

const emptyForm = {
  title: '', department: '', location: '', type: 'Full-time', experience: '', apply_link: '', sort_order: 0,
};

function IconEdit() {
  return (
    <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
      <path d="M14.2 2.8a1.8 1.8 0 012.5 2.5L6 15.9l-3.4.9.9-3.4L14.2 2.8z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}
function IconTrash() {
  return (
    <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
      <path d="M4 6h12M8 6V4.5a1 1 0 011-1h2a1 1 0 011 1V6m-7 0l.7 9.2a1.5 1.5 0 001.5 1.4h4.6a1.5 1.5 0 001.5-1.4L15 6"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function AdminCareers() {
  const navigate = useNavigate();
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [form, setForm]         = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving]       = useState(false);
  const [toast, setToast]         = useState(null);

  useEffect(() => {
    if (!isLoggedIn()) { navigate('/admin', { replace: true }); return; }
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadItems = () => {
    setLoading(true);
    fetchCareers()
      .then(setItems)
      .catch(() => setToast({ type: 'err', text: 'Could not load job openings.' }))
      .finally(() => setLoading(false));
  };

  const handleLogout = () => { clearToken(); navigate('/admin'); };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title || '',
      department: item.department || '',
      location: item.location || '',
      type: item.type || 'Full-time',
      experience: item.experience || '',
      apply_link: item.applyLink || '',
      sort_order: 0,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setToast({ type: 'err', text: 'Title is required.' }); return; }

    setSaving(true);
    setToast(null);
    try {
      if (editingId) {
        await updateCareer(editingId, form);
        setToast({ type: 'ok', text: 'Position updated.' });
      } else {
        await createCareer(form);
        setToast({ type: 'ok', text: 'Position added.' });
      }
      resetForm();
      loadItems();
    } catch (err) {
      setToast({ type: 'err', text: err.message || 'Save failed.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this job opening? This cannot be undone.')) return;
    try {
      await deleteCareer(id);
      setToast({ type: 'ok', text: 'Deleted.' });
      loadItems();
      if (editingId === id) resetForm();
    } catch (err) {
      setToast({ type: 'err', text: err.message || 'Delete failed.' });
    }
  };

  return (
    <div className="adm">
      <div className="adm-topbar">
        <h1 className="adm-topbar__title">Careers — Admin</h1>
        <button className="adm-logout" onClick={handleLogout}>Sign Out</button>
      </div>

      <AdminNav active="careers" />

      <div className="adm-body">

        {/* ── Form ── */}
        <div className="adm-card">
          <h2 className="adm-card__title">{editingId ? 'Edit Position' : 'Add New Position'}</h2>

          {toast && (
            <div className={`adm-toast adm-toast--${toast.type}`}>{toast.text}</div>
          )}

          <form className="adm-form-grid" onSubmit={handleSubmit}>
            <div className="adm-field">
              <label htmlFor="title">Job Title *</label>
              <input id="title" name="title" value={form.title} onChange={handleChange}
                     placeholder="e.g. Frontend Developer" />
            </div>

            <div className="adm-row-2">
              <div className="adm-field">
                <label htmlFor="department">Department</label>
                <input id="department" name="department" value={form.department} onChange={handleChange}
                       placeholder="e.g. Engineering" />
              </div>
              <div className="adm-field">
                <label htmlFor="location">Location</label>
                <input id="location" name="location" value={form.location} onChange={handleChange}
                       placeholder="e.g. Coimbatore" />
              </div>
            </div>

            <div className="adm-row-2">
              <div className="adm-field">
                <label htmlFor="type">Job Type</label>
                <select id="type" name="type" value={form.type} onChange={handleChange}>
                  <option value="Full-time">Full-time</option>
                  <option value="Part-time">Part-time</option>
                  <option value="Internship">Internship</option>
                  <option value="Contract">Contract</option>
                </select>
              </div>
              <div className="adm-field">
                <label htmlFor="experience">Experience</label>
                <input id="experience" name="experience" value={form.experience} onChange={handleChange}
                       placeholder="e.g. 1-3 years" />
              </div>
            </div>

            <div className="adm-field">
              <label htmlFor="apply_link">Apply Link</label>
              <input id="apply_link" name="apply_link" value={form.apply_link} onChange={handleChange}
                     placeholder="e.g. https://forms.gle/... or mailto:hr@yourcompany.com" />
            </div>

            <div className="adm-field">
              <label htmlFor="sort_order">Display Order (lower shows first)</label>
              <input id="sort_order" name="sort_order" type="number" value={form.sort_order} onChange={handleChange} />
            </div>

            <div className="adm-form-actions">
              <button className="adm-btn" type="submit" disabled={saving}>
                {saving ? 'Saving…' : editingId ? 'Update Position' : 'Add Position'}
              </button>
              {editingId && (
                <button type="button" className="adm-btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        {/* ── List ── */}
        <div className="adm-card">
          <h2 className="adm-card__title">All Positions ({items.length})</h2>

          {loading && <p className="adm-empty">Loading…</p>}
          {!loading && items.length === 0 && <p className="adm-empty">No job openings added yet.</p>}

          {!loading && items.length > 0 && (
            <div className="adm-list">
              {items.map((item) => (
                <div className="adm-item" key={item.id}>
                  <div className="adm-item__body">
                    <p className="adm-item__title">{item.title}</p>
                    <p className="adm-item__meta">
                      {[item.department, item.location, item.type, item.experience].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  <div className="adm-item__actions">
                    <button className="adm-icon-btn" onClick={() => startEdit(item)} aria-label="Edit"><IconEdit /></button>
                    <button className="adm-icon-btn adm-icon-btn--danger" onClick={() => handleDelete(item.id)} aria-label="Delete"><IconTrash /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}